import { Loader2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import {
  CardActionButton,
  DisconnectFooter,
  IntegrationCardShell,
  StatusPill,
} from "@/components/dashboard/IntegrationCardShell";

const DEFAULT_STALE_MS = 30 * 1000;

interface ComposioIntegrationPanelProps {
  description: string;
  displayName: string;
  icon: LucideIcon;
  integration:
    | {
        connectedAt?: number;
        status: string;
        lastError?: string;
        config?: unknown;
      }
    | null
    | undefined;
  onDisconnect: () => Promise<unknown>;
  onStartOAuth: () => Promise<unknown>;
  staleThresholdMs?: number;
}

export function ComposioIntegrationPanel({
  description,
  displayName,
  icon,
  integration,
  onDisconnect,
  onStartOAuth,
  staleThresholdMs = DEFAULT_STALE_MS,
}: ComposioIntegrationPanelProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const currentStatus = integration?.status;
  const connectingAt = integration?.connectedAt;
  const config = integration?.config as { redirectUrl?: string } | null;

  // Force re-render when stale timeout elapses
  const [, setTick] = useState(0);
  useEffect(() => {
    if (currentStatus !== "connecting" || connectingAt == null) return;
    const remaining = staleThresholdMs - (Date.now() - connectingAt);
    if (remaining <= 0) return;
    const timer = setTimeout(() => setTick((t) => t + 1), remaining + 100);
    return () => clearTimeout(timer);
  }, [currentStatus, connectingAt, staleThresholdMs]);

  // Redirect to Composio OAuth when redirectUrl appears
  useEffect(() => {
    if (currentStatus === "connecting" && config?.redirectUrl && isSubmitting) {
      // Defer to next tick so React state settles before navigation —
      // prevents the browser "Leave site?" dialog from firing
      setTimeout(() => {
        window.location.href = config.redirectUrl!;
      }, 0);
    }
  }, [currentStatus, config?.redirectUrl, isSubmitting]);

  async function handleConnect() {
    setIsSubmitting(true);
    try {
      await onStartOAuth();
    } catch {
      setIsSubmitting(false);
    }
  }

  async function handleDisconnect() {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
      setShowDisconnectConfirm(false);
    } catch (err) {
      console.error(`${displayName} disconnect failed:`, err);
    } finally {
      setIsDisconnecting(false);
    }
  }

  const isStaleConnecting =
    currentStatus === "connecting" &&
    connectingAt != null &&
    Date.now() - connectingAt > staleThresholdMs;

  // Disconnected / null / stale connecting
  if (!integration || currentStatus === "disconnected" || isStaleConnecting) {
    return (
      <IntegrationCardShell
        icon={icon}
        title={displayName}
        description={description}
        footer={
          <CardActionButton tone="tonal" disabled={isSubmitting} onClick={handleConnect}>
            {isSubmitting ? "Connecting…" : "Connect"}
          </CardActionButton>
        }
      />
    );
  }

  // Connecting — same skeleton, spinner in the footer slot; no layout jump
  if (currentStatus === "connecting") {
    return (
      <IntegrationCardShell
        icon={icon}
        title={displayName}
        pill={<StatusPill variant="pending">Connecting…</StatusPill>}
        description={description}
        footer={
          <div className="flex items-center justify-center gap-2 py-2.5 font-body text-sm text-home-ink/55 dark:text-white/55">
            <Loader2 className="h-4 w-4 animate-spin" />
            Waiting for authorization
          </div>
        }
      />
    );
  }

  // Error — calm and human. The raw provider error stays in Convex, never
  // in the DOM; a technical buyer screenshotting this page sees no internals.
  if (currentStatus === "error") {
    return (
      <IntegrationCardShell
        icon={icon}
        title={displayName}
        pill={<StatusPill variant="attention">Action needed</StatusPill>}
        description={`Your ${displayName} connection isn't working. Reconnect to keep it running.`}
        footer={
          <CardActionButton tone="primary" disabled={isSubmitting} onClick={handleConnect}>
            {isSubmitting ? "Connecting…" : "Reconnect"}
          </CardActionButton>
        }
      />
    );
  }

  // Connected
  const connectedLabel = integration?.connectedAt
    ? `Connected · ${new Date(integration.connectedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : "Connected";

  return (
    <IntegrationCardShell
      icon={icon}
      title={displayName}
      pill={<StatusPill variant="active">{connectedLabel}</StatusPill>}
      description={description}
      footer={
        <DisconnectFooter
          confirming={showDisconnectConfirm}
          disconnecting={isDisconnecting}
          onConfirmChange={setShowDisconnectConfirm}
          onDisconnect={handleDisconnect}
        />
      }
    />
  );
}
